import { Plus } from "lucide-react";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

/**
 * Indeterminate "Transcribing audio" progress indicator (LAT-623). Replaces the easy-to-miss
 * mic spinner: it takes over the same composer row the recording waveform used, so
 * recording → transcribing reads as one continuous flow. There is no real percentage to show
 * (the transcription action is a one-shot await), so the bar is intentionally indeterminate —
 * a gradient segment sweeps across the track. See `.transcription-sweep-segment` in index.css.
 */
export function TranscribingIndicator({
  className,
  label = "Transcribing audio",
  showLeadingPlaceholder = true,
}: {
  className?: string;
  label?: string;
  /** Render the disabled "+" placeholder so the row left-aligns with the recording strip. */
  showLeadingPlaceholder?: boolean;
}) {
  return (
    <output
      className={cn("flex w-full items-center gap-2.5", className)}
      role="progressbar"
      aria-busy="true"
      aria-label={label}
    >
      {showLeadingPlaceholder ? (
        <Button
          type="button"
          variant="ghost"
          size="icon-sm"
          className="size-7 shrink-0 p-0 text-muted-foreground/50"
          disabled
          aria-hidden="true"
          tabIndex={-1}
        >
          <Plus className="size-[18px]" />
        </Button>
      ) : null}
      {/* Match the recording-strip timer exactly (text-xs, normal weight, muted) so the
          label reads consistently across recording → transcribing and every surface. */}
      <span aria-hidden="true" className="shrink-0 text-xs text-muted-foreground">
        {label}
      </span>
      <div
        aria-hidden="true"
        className="relative h-1 min-w-0 flex-1 overflow-hidden rounded-full bg-muted"
      >
        <div className="transcription-sweep-segment" />
      </div>
    </output>
  );
}
